import { ElementRef, Injectable, ViewContainerRef } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { IDictionaryValue } from '@cinetixx/cinetixx-ui';
import { IZone } from '../../../../prices/core/interfaces';
import { CreatorSettingsViewsKeys, CreatorViewsKeys, CreatorViewsValuesKeys, ShapeKeys, ShapeTypes } from '../../enums';
import { ICreatorViewsValues, ILibraryImageElement, IPriorityShape, ISeatsPlan, ISectorElement, IShape } from '../../interfaces';
import { BaseElementManager, ImageManager, SeatManager, ShapeAttributesManager } from '../../managers';
import { CreatorSteps, CreatorViewsMapType, GuidWrapper } from '../../types';

@Injectable({
  providedIn: 'root'
})
export class CreatorStateService {

  public static currentConfigurationName = '';
  public static pickedConfiguration: IDictionaryValue = null;
  public static roomRef: ElementRef<HTMLDivElement>;
  public static roomTemplate: ViewContainerRef;
  public static sectorId: number;
  public static parentSectorId: number;
  public static isConfigured = false;
  public static movedToFirstSector = false;
  public static seatTypes: IDictionaryValue[] = [];
  public static currentConfigurationSectors: GuidWrapper<ISectorElement>[] = [];
  public static seatStatuses: IDictionaryValue[] = [];
  public static initSeatsPlanState: ISeatsPlan = null;
  public static initPriorityShape: IPriorityShape = null;
  public static currentPriority = 100;
  public static deleteElementsIds: number[] = [];
  public static seatsGuidsWithManualPriority: string[] = [];
  public static shapesToCopy: HTMLElement[] = [];
  public static isConfigurationDirty$ = new BehaviorSubject<boolean>(false);
  public static hadConfigurationError$ = new BehaviorSubject<boolean>(false);
  public static isPriorityDirty$ = new BehaviorSubject<boolean>(false);
  public static showPriorityDensity = true;

  private static readonly _creatorStateActionsMap = new Map<keyof ICreatorViewsValues, (...args: any) => unknown>(
    [
      [CreatorViewsKeys.ROWS, CreatorStateService.updateCurveState.bind(CreatorStateService)],
      [CreatorViewsKeys.SEATS, CreatorStateService.updateSeatsState.bind(CreatorStateService)],
      [CreatorViewsKeys.BASE_ELEMENTS, CreatorStateService.updateBaseElementsState.bind(CreatorStateService)],
      [CreatorViewsKeys.SECTORS, CreatorStateService.updateSectorElementsState.bind(CreatorStateService)],
    ]
  );

  private static _showRowsInConnectionsCreator = false;
  private static readonly _showRowsInConnectionsCreator$ = new BehaviorSubject<boolean>(CreatorStateService._showRowsInConnectionsCreator);

  private static _settingsView = CreatorSettingsViewsKeys.GLOBAL;
  private static readonly _settingsView$ = new BehaviorSubject<CreatorSettingsViewsKeys>(CreatorStateService._settingsView);

  private static _seatConnections: 'left' | 'right';
  private static readonly _seatConnections$ = new BehaviorSubject<'left' | 'right'>(CreatorStateService._seatConnections);

  private static _stepDisabledState = new Map<CreatorSteps, boolean>(
    [
      ['seats-plan', true],
      ['connections', true],
      ['zones', true],
      ['seat-priority', true]
    ]
  );
  private static readonly _stepDisabledState$ = new BehaviorSubject<Map<CreatorSteps, boolean>>(CreatorStateService._stepDisabledState);

  private static _isManualPrioritySetting = false;
  private static readonly _isManualPrioritySetting$ = new BehaviorSubject<boolean>(CreatorStateService._isManualPrioritySetting);

  private static _isLoading = false;
  private static readonly _isLoading$ = new BehaviorSubject<boolean>(CreatorStateService._isLoading);

  private static _priorityShape: IPriorityShape = null;
  private static readonly _priorityShape$ = new BehaviorSubject<IPriorityShape>(CreatorStateService._priorityShape);

  private static _hasPlannedShows = false;
  private static readonly _hasPlannedShows$ = new BehaviorSubject<boolean>(CreatorStateService._hasPlannedShows);

  private static _currentStep: CreatorSteps = null;
  private static readonly _currentStep$ = new BehaviorSubject<CreatorSteps>(CreatorStateService._currentStep);

  private static _seatsPlan: ISeatsPlan = null;
  private static readonly _seatsPlan$ = new BehaviorSubject<ISeatsPlan>(CreatorStateService._seatsPlan);

  private static _libraryImageElements: ILibraryImageElement[] = [];
  private static readonly _libraryImageElements$ = new BehaviorSubject<ILibraryImageElement[]>(CreatorStateService._libraryImageElements);

  private static _shapes: IShape[] = [];
  private static readonly _shapes$ = new BehaviorSubject<IShape[]>(CreatorStateService._shapes);

  private static _libraryShapes: IShape[] = [];
  private static readonly _libraryShapes$ = new BehaviorSubject<IShape[]>(CreatorStateService._libraryShapes);

  private static _sectorsShapes: IShape[] = [];
  private static readonly _sectorsShapes$ = new BehaviorSubject<IShape[]>(CreatorStateService._sectorsShapes);

  private static _configurations: IDictionaryValue[] = null;
  private static readonly _configurations$ = new BehaviorSubject<IDictionaryValue[]>(CreatorStateService._configurations);

  private static _zones: IZone[] = [];
  private static readonly _zones$ = new BehaviorSubject<IZone[]>(CreatorStateService._zones);

  private static _creatorState: CreatorViewsMapType = new Map<keyof ICreatorViewsValues, ICreatorViewsValues[CreatorViewsKeys][CreatorViewsValuesKeys]>();

  private static readonly _creatorState$ = new BehaviorSubject<CreatorViewsMapType>(CreatorStateService._creatorState);

  public static emitSeatsPlanStateChange(): void {
    CreatorStateService._seatsPlan$.next(CreatorStateService._seatsPlan);
  }

  public static clearCurrentState(): void {
    CreatorStateService.deleteElementsIds = [];
    CreatorStateService.shapesToCopy = [];
  }

  public static changeStepDisabledState(step: CreatorSteps, value: boolean): void {
    CreatorStateService._stepDisabledState.set(step, value);
    CreatorStateService._stepDisabledState$.next(CreatorStateService._stepDisabledState);
  }

  public static get showRowsInConnectionsCreator(): boolean {
    return CreatorStateService._showRowsInConnectionsCreator;
  }

  public static get showRowsInConnectionsCreator$(): Observable<boolean> {
    return CreatorStateService._showRowsInConnectionsCreator$.asObservable();
  }

  public static set showRowsInConnectionsCreator(value: boolean) {
    CreatorStateService._showRowsInConnectionsCreator = value;
    CreatorStateService._showRowsInConnectionsCreator$.next(value);
  }

  public static get stepDisabledState(): Map<CreatorSteps, boolean> {
    return CreatorStateService._stepDisabledState;
  }

  public static get stepDisabledState$(): Observable<Map<CreatorSteps, boolean>> {
    return CreatorStateService._stepDisabledState$.asObservable();
  }

  public static get isManualPrioritySetting(): boolean {
    return CreatorStateService._isManualPrioritySetting;
  }

  public static get isManualPrioritySetting$(): Observable<boolean> {
    return CreatorStateService._isManualPrioritySetting$.asObservable();
  }

  public static set isManualPrioritySetting(value: boolean) {
    CreatorStateService._isManualPrioritySetting = value;
    CreatorStateService._isManualPrioritySetting$.next(value);
  }

  public static get hasPlannedShows(): boolean {
    return CreatorStateService._hasPlannedShows;
  }

  public static get hasPlannedShows$(): Observable<boolean> {
    return CreatorStateService._hasPlannedShows$.asObservable();
  }

  public static set hasPlannedShows(value: boolean) {
    CreatorStateService._hasPlannedShows = value;
    CreatorStateService._hasPlannedShows$.next(value);
  }

  public static get seatConnections(): 'left' | 'right' {
    return CreatorStateService._seatConnections;
  }

  public static get seatConnections$(): Observable<'left' | 'right'> {
    return CreatorStateService._seatConnections$.asObservable();
  }

  public static set seatConnections(value: 'left' | 'right') {
    CreatorStateService._seatConnections = value;
    CreatorStateService._seatConnections$.next(value);
  }

  public static get priorityShape(): IPriorityShape {
    return CreatorStateService._priorityShape;
  }

  public static get priorityShape$(): Observable<IPriorityShape> {
    return CreatorStateService._priorityShape$.asObservable();
  }

  public static set priorityShape(value: IPriorityShape) {
    CreatorStateService._priorityShape = value;
    CreatorStateService._priorityShape$.next(value);
  }

  public static get isLoading(): boolean {
    return CreatorStateService._isLoading;
  }

  public static get isLoading$(): Observable<boolean> {
    return CreatorStateService._isLoading$.asObservable();
  }

  public static set isLoading(value: boolean) {
    CreatorStateService._isLoading = value;
    CreatorStateService._isLoading$.next(value);
  }

  public static get currentStep(): CreatorSteps {
    return CreatorStateService._currentStep;
  }

  public static get currentStep$(): Observable<CreatorSteps> {
    return CreatorStateService._currentStep$.asObservable();
  }

  public static set currentStep(value: CreatorSteps) {
    CreatorStateService._currentStep = value;
    CreatorStateService._currentStep$.next(value);
  }

  public static get seatsPlan(): ISeatsPlan {
    return CreatorStateService._seatsPlan;
  }

  public static get seatsPlan$(): Observable<ISeatsPlan> {
    return CreatorStateService._seatsPlan$.asObservable();
  }

  public static set seatsPlan(value: ISeatsPlan) {
    if (value) {
      value.seats = value.seats?.length ? value.seats.map(SeatManager.formatObject) : [];
      value.baseElements = value.baseElements?.length ? value.baseElements.map(BaseElementManager.formatObject) : [];
      value.images = value.images?.length ? value.images.map(ImageManager.formatObject) : [];
    }

    CreatorStateService._seatsPlan = value;
    CreatorStateService._seatsPlan$.next(value);
  }

  public static get libraryImageElements(): ILibraryImageElement[] {
    return CreatorStateService._libraryImageElements;
  }

  public static get libraryImageElements$(): Observable<ILibraryImageElement[]> {
    return CreatorStateService._libraryImageElements$.asObservable();
  }

  public static set libraryImageElements(libraryImageElements: ILibraryImageElement[]) {
    CreatorStateService._libraryImageElements = libraryImageElements;
    CreatorStateService._libraryImageElements$.next(libraryImageElements);
  }

  public static get shapes(): IShape[] {
    return CreatorStateService._shapes;
  }

  public static get shapes$(): Observable<IShape[]> {
    return CreatorStateService._shapes$.asObservable();
  }

  public static set shapes(shapes: IShape[]) {
    CreatorStateService._shapes = shapes;
    CreatorStateService._shapes$.next(shapes);
  }

  public static get sectorsShapes(): IShape[] {
    return CreatorStateService._sectorsShapes;
  }

  public static get sectorsShapes$(): Observable<IShape[]> {
    return CreatorStateService._sectorsShapes$.asObservable();
  }

  public static set sectorsShapes(shapes: IShape[]) {
    CreatorStateService._sectorsShapes = shapes;
    CreatorStateService._sectorsShapes$.next(shapes);
    CreatorStateService.shapes = [...CreatorStateService._libraryShapes, ...CreatorStateService._sectorsShapes];
  }

  public static get libraryShapes(): IShape[] {
    return CreatorStateService._libraryShapes;
  }

  public static get libraryShapes$(): Observable<IShape[]> {
    return CreatorStateService._libraryShapes$.asObservable();
  }

  public static set libraryShapes(shapes: IShape[]) {
    CreatorStateService._libraryShapes = shapes;
    CreatorStateService._libraryShapes$.next(shapes);

    CreatorStateService.shapes = [...CreatorStateService._libraryShapes, ...CreatorStateService._sectorsShapes];
  }

  public static get configurations(): IDictionaryValue[] {
    return CreatorStateService._configurations;
  }

  public static get configurations$(): Observable<IDictionaryValue[]> {
    return CreatorStateService._configurations$.asObservable();
  }

  public static set configurations(configurations: IDictionaryValue[]) {
    CreatorStateService._configurations = configurations;
    CreatorStateService._configurations$.next(configurations);
  }

  public static get zones(): IZone[] {
    return CreatorStateService._zones;
  }

  public static get zones$(): Observable<IZone[]> {
    return CreatorStateService._zones$.asObservable();
  }

  public static set zones(zones: IZone[]) {
    CreatorStateService._zones = zones;
    CreatorStateService._zones$.next(zones);
  }

  public static get settingsView(): CreatorSettingsViewsKeys {
    return CreatorStateService._settingsView;
  }

  public static get settingsView$(): Observable<CreatorSettingsViewsKeys> {
    return CreatorStateService._settingsView$.asObservable();
  }

  public static set settingsView(view: CreatorSettingsViewsKeys) {
    CreatorStateService._settingsView = view;
    CreatorStateService._settingsView$.next(view);
  }

  public static get creatorState$(): Observable<CreatorViewsMapType> {
    return CreatorStateService._creatorState$.asObservable();
  }

  public static updateCreatorState<K extends keyof ICreatorViewsValues>(key: K, value: ICreatorViewsValues[K][CreatorViewsValuesKeys.INPUT]): void {
    if (CreatorStateService._creatorStateActionsMap.has(key)) {
      CreatorStateService._creatorStateActionsMap.get(key)(value);
      CreatorStateService._creatorState$.next(this._creatorState);
    }
  }

  public static getCreatorStateValue<K extends keyof ICreatorViewsValues>(key: K): ICreatorViewsValues[K][CreatorViewsValuesKeys.STATE] {
    return CreatorStateService._creatorState.get(key) as ICreatorViewsValues[K][CreatorViewsValuesKeys.STATE];
  }

  public static get seatsElements(): HTMLElement[] {
    return Array.from(
      CreatorStateService.roomRef.nativeElement?.querySelectorAll<HTMLElement>(
        `app-shape[${ShapeAttributesManager.getAttribute(ShapeKeys.SHAPE_TYPE)}="${ShapeTypes.PLACE_SHAPE}"]`
      )
    ) ?? [];
  }

  /**
   * seats plan state methods
   */

  private static updateCurveState(values: ICreatorViewsValues[CreatorViewsKeys.ROWS][CreatorViewsValuesKeys.INPUT]): void {
    CreatorStateService._creatorState.set(CreatorViewsKeys.ROWS, values.map(x => {
      const seats = x ? CreatorStateService._seatsPlan?.seats?.filter(seat => seat?.rankToken === x) : [];
      return {
        seats,
        seatElements: seats.map(x => SeatManager.getHTMLElement(x.guid))
      };
    }));
  }

  private static updateSeatsState(els: ICreatorViewsValues[CreatorViewsKeys.SEATS][CreatorViewsValuesKeys.INPUT]): void {
    CreatorStateService._creatorState.set(CreatorViewsKeys.SEATS, {
      seatElements: els,
      seats: els.map(el => CreatorStateService._seatsPlan.seats.find(x => x.guid === ShapeAttributesManager.getGUID(el)))
    });
  }

  private static updateBaseElementsState(els: ICreatorViewsValues[CreatorViewsKeys.BASE_ELEMENTS][CreatorViewsValuesKeys.INPUT]): void {
    CreatorStateService._creatorState.set(CreatorViewsKeys.BASE_ELEMENTS, {
      baseHTMLElements: els,
      baseElements: els.map(el => CreatorStateService._seatsPlan.baseElements.find(x => x.guid === ShapeAttributesManager.getGUID(el)))
    });
  }

  private static updateSectorElementsState(els: ICreatorViewsValues[CreatorViewsKeys.SECTORS][CreatorViewsValuesKeys.INPUT]): void {
    CreatorStateService._creatorState.set(CreatorViewsKeys.SECTORS, {
      sectorHTMLElements: els,
      sectorElements: els.map(el => CreatorStateService._seatsPlan.sectors.find(x => x.guid === ShapeAttributesManager.getGUID(el)))
    });
  }
}
